/* /portaal/weekstory — Instagram-story-achtige swipeable memory-card met
   top-3 foto's + hoogtepunten van de week per kind. Voor gebruik op vrijdag
   en het weekend als 'lookback'-moment voor ouders. */
import { useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import api, { formatApiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, ChevronLeft, ChevronRight, Sparkles, Heart, Smile, Meh, Frown } from "lucide-react";

const BACKEND_ROOT = API.replace(/\/api$/, "");
const STEMMING = { blij: Smile, neutraal: Meh, moe: Meh, verdrietig: Frown };

export default function PortaalWeekstoryPage() {
  const { user, status } = useAuth();
  const navigate = useNavigate();
  const [kids, setKids] = useState([]);
  const [selKid, setSelKid] = useState(null);
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [idx, setIdx] = useState(0);
  const startX = useRef(null);

  useEffect(() => {
    if (status !== "authed" || user?.role !== "ouder") return;
    (async () => {
      try {
        const ov = await api.get("/portaal/overview");
        const list = ov.data.kinderen || [];
        setKids(list);
        if (list.length) setSelKid(list[0]);
      } catch (e) { toast.error(formatApiError(e?.response?.data?.detail || e.message)); }
    })();
  }, [status, user]);

  useEffect(() => {
    if (!selKid) return;
    setLoading(true);
    setIdx(0);
    api.get(`/portaal/kind/${selKid.id}/weekstory`)
      .then(r => setStory(r.data))
      .catch(e => toast.error(formatApiError(e?.response?.data?.detail || e.message)))
      .finally(() => setLoading(false));
  }, [selKid]);

  // Slides: intro + N photos + N hoogtepunten + outro
  const slides = useMemo(() => {
    if (!story) return [];
    const s = [];
    s.push({ type: "intro" });
    (story.photos || []).forEach(p => s.push({ type: "photo", data: p }));
    (story.hoogtepunten || []).forEach(h => s.push({ type: "highlight", data: h }));
    s.push({ type: "outro" });
    return s;
  }, [story]);

  useEffect(() => {
    if (slides.length === 0) return;
    // Auto-advance elke 5s
    const t = setTimeout(() => setIdx(i => Math.min(i + 1, slides.length - 1)), 5500);
    return () => clearTimeout(t);
  }, [idx, slides.length]);

  const next = () => setIdx(i => Math.min(i + 1, slides.length - 1));
  const prev = () => setIdx(i => Math.max(i - 1, 0));

  if (status === "loading") return null;
  if (status !== "authed" || user?.role !== "ouder") return <Navigate to="/login" replace />;

  const cur = slides[idx];
  const primair = "#3a5a40";

  return (
    <div className="fixed inset-0 bg-black flex flex-col z-50" data-testid="portaal-weekstory-page">
      {/* Progress bar */}
      <div className="pt-3 px-3 flex gap-1">
        {slides.map((_, i) => (
          <div key={i} className="flex-1 h-1 rounded-full bg-white/20 overflow-hidden">
            <div
              className={`h-full bg-white ${i < idx ? "w-full" : i === idx ? "w-full animate-[progress_5s_linear]" : "w-0"}`}
              style={i === idx ? { animation: "progress 5s linear forwards" } : {}}
            />
          </div>
        ))}
      </div>

      {/* Header */}
      <div className="px-4 py-3 flex items-center gap-3 text-white">
        <Button variant="ghost" size="icon" onClick={() => navigate("/portaal")} className="text-white hover:bg-white/10" data-testid="weekstory-back"><ArrowLeft className="h-5 w-5" /></Button>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold">Deze week met {selKid?.voornaam}</div>
          {story && <div className="text-xs opacity-70 font-mono">{story.week_start} — {story.week_end}</div>}
        </div>
        {kids.length > 1 && (
          <select
            className="bg-white/10 text-white border border-white/20 rounded-md text-xs px-2 py-1"
            value={selKid?.id || ""}
            onChange={e => setSelKid(kids.find(k => k.id === e.target.value))}
            data-testid="weekstory-kid-select"
          >{kids.map(k => <option key={k.id} value={k.id} className="text-black">{k.voornaam}</option>)}</select>
        )}
      </div>

      {/* Story canvas */}
      <div
        className="flex-1 relative overflow-hidden"
        onTouchStart={e => { startX.current = e.touches[0].clientX; }}
        onTouchEnd={e => {
          if (startX.current == null) return;
          const dx = e.changedTouches[0].clientX - startX.current;
          if (dx < -40) next();
          else if (dx > 40) prev();
          startX.current = null;
        }}
        onClick={e => {
          const rect = e.currentTarget.getBoundingClientRect();
          if (e.clientX - rect.left < rect.width / 3) prev();
          else next();
        }}
        data-testid="weekstory-canvas"
      >
        {loading && <div className="absolute inset-0 flex items-center justify-center text-white text-sm">Laden…</div>}

        {!loading && story && slides.length <= 2 && (
          <div className="absolute inset-0 flex items-center justify-center px-6 text-center text-white">
            <div>
              <div className="text-2xl mb-2">📸</div>
              <div className="text-lg font-semibold mb-1">Nog geen verhaal deze week</div>
              <div className="text-sm opacity-70">Zodra de leidsters foto's of hoogtepunten delen verschijnen ze hier — kom vrijdag terug voor de weekterugblik.</div>
            </div>
          </div>
        )}

        {!loading && cur?.type === "intro" && story && (
          <div className="absolute inset-0 flex items-center justify-center px-6 text-center" style={{ background: `linear-gradient(160deg, ${primair}, #6b8e5a)` }} data-testid="weekstory-intro">
            <div className="text-white">
              <Sparkles className="h-10 w-10 mx-auto mb-4 opacity-80" />
              <div className="text-3xl font-display mb-2">De week van {selKid?.voornaam}</div>
              <div className="text-sm opacity-80 mb-8">{new Date(story.week_start).toLocaleDateString("nl-NL", { day: "numeric", month: "long" })} — {new Date(story.week_end).toLocaleDateString("nl-NL", { day: "numeric", month: "long" })}</div>
              <div className="grid grid-cols-2 gap-3 max-w-xs mx-auto">
                <StatChip label="Foto's" value={story.n_photos_total} />
                <StatChip label="Schriftjes" value={story.n_schriftjes} />
                {story.stemming_top && (() => { const Ic = STEMMING[story.stemming_top] || Smile; return (
                  <StatChip label="Meestal" value={<span className="flex items-center gap-1 capitalize"><Ic className="h-4 w-4" />{story.stemming_top}</span>} />
                );})()}
                <StatChip label="Hoogtepunten" value={(story.hoogtepunten || []).length} />
              </div>
              <div className="text-xs opacity-70 mt-8">Tik rechts om verder →</div>
            </div>
          </div>
        )}

        {!loading && cur?.type === "photo" && (
          <div className="absolute inset-0 flex flex-col" data-testid="weekstory-photo">
            <img src={`${BACKEND_ROOT}${cur.data.url}`} alt="week" className="flex-1 object-cover w-full" />
            {cur.data.caption && (
              <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/90 to-transparent text-white">
                <div className="text-lg font-semibold leading-tight">{cur.data.caption}</div>
                <div className="text-xs opacity-80 mt-1">{(cur.data.created_at || "").slice(0, 10)} · {cur.data.author_name || "Team"}</div>
              </div>
            )}
          </div>
        )}

        {!loading && cur?.type === "highlight" && (
          <div className="absolute inset-0 flex items-center justify-center px-8 text-center" style={{ background: "linear-gradient(160deg, #c9a86a, #d97757)" }} data-testid="weekstory-highlight">
            <div className="text-white max-w-sm">
              <Heart className="h-9 w-9 mx-auto mb-3 opacity-80" />
              <div className="text-2xl font-display leading-snug mb-3">"{cur.data.tekst}"</div>
              <div className="text-xs opacity-80">{cur.data.datum} · {cur.data.van_naam || "Team"}</div>
            </div>
          </div>
        )}

        {!loading && cur?.type === "outro" && (
          <div className="absolute inset-0 flex items-center justify-center px-8 text-center" style={{ background: `linear-gradient(160deg, ${primair}, #2D4A3E)` }} data-testid="weekstory-outro">
            <div className="text-white">
              <div className="text-6xl mb-4">🌸</div>
              <div className="text-2xl font-display mb-2">Fijn weekend!</div>
              <div className="text-sm opacity-80 mb-6">Tot maandag — dan gaan we weer op avontuur.</div>
              <Button onClick={() => navigate("/portaal/album")} className="bg-white text-neutral-900 hover:bg-white/90" data-testid="weekstory-to-album">
                Naar het foto-album
              </Button>
            </div>
          </div>
        )}

        {/* Nav arrows (desktop hint) */}
        <button className="absolute inset-y-0 left-0 w-1/3 hidden md:flex items-center px-4 text-white/50 hover:text-white transition-colors" onClick={e => { e.stopPropagation(); prev(); }} data-testid="weekstory-prev"><ChevronLeft className="h-6 w-6" /></button>
        <button className="absolute inset-y-0 right-0 w-1/3 hidden md:flex items-center justify-end px-4 text-white/50 hover:text-white transition-colors" onClick={e => { e.stopPropagation(); next(); }} data-testid="weekstory-next"><ChevronRight className="h-6 w-6" /></button>
      </div>

      <style>{`
        @keyframes progress { from { width: 0; } to { width: 100%; } }
      `}</style>
    </div>
  );
}

function StatChip({ label, value }) {
  return (
    <div className="bg-white/10 border border-white/20 rounded-lg p-3 text-white">
      <div className="text-xs uppercase tracking-widest opacity-70">{label}</div>
      <div className="text-xl font-display mt-0.5">{value}</div>
    </div>
  );
}
